import * as React from 'react';
import { IMapIssue, mapIssueView } from './mapHealth';

/* An error's technical details (see errorDetails), hidden until the user asks for them, with a
 * copy button for support requests. */
export const ErrorDetails: React.FC<{ lines: string[] }> = ({ lines }) => {
    const [open, setOpen] = React.useState(false);
    if (lines.length === 0) {
        return null;
    }
    return (
        <div className="map-error-details">
            <button type="button" className="map-error-details-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
                {open ? 'Hide details' : 'Details'}
            </button>
            {open && (
                <>
                    <pre className="map-error-details-text">{lines.join('\n')}</pre>
                    <button
                        type="button"
                        className="map-error-details-copy"
                        onClick={() => {
                            try {
                                void navigator.clipboard?.writeText(lines.join('\n'));
                            } catch {
                                /* clipboard unavailable; the details are still selectable text */
                            }
                        }}
                    >
                        Copy details
                    </button>
                </>
            )}
        </div>
    );
};

/* The marks in front of a card's title, as system dialogs show them: a red circle with a white cross
 * for a failure, a yellow triangle for something unavailable that may not be a failure. */
const ErrorMark: React.FC = () => (
    <svg className="map-block-icon" width="22" height="22" viewBox="0 0 22 22" aria-hidden="true" focusable="false">
        <circle cx="11" cy="11" r="10" fill="#d13438" />
        <path d="M7.5 7.5l7 7m0-7l-7 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const WarningMark: React.FC = () => (
    <svg className="map-block-icon" width="22" height="22" viewBox="0 0 22 22" aria-hidden="true" focusable="false">
        <path d="M11 2L21 20H1z" fill="#f2b705" stroke="#c98f00" strokeWidth="1" strokeLinejoin="round" />
        <path d="M11 8v5.5" stroke="#1f1f1f" strokeWidth="2" strokeLinecap="round" />
        <circle cx="11" cy="16.6" r="1.2" fill="#1f1f1f" />
    </svg>
);

/* Grays out the Mapping page under a card saying what's wrong. The tract layer can be unavailable
 * because the project has no mapping, so it gets the warning mark; a map that isn't set up yet gets
 * none. */
export const MapBlockOverlay: React.FC<{ issue: IMapIssue; details: string[]; onRetry?: () => void }> = ({ issue, details, onRetry }) => {
    const view = mapIssueView(issue);
    return (
        <div className="map-block-overlay">
            <div
                className="map-block-card"
                role="alertdialog"
                aria-labelledby="map-block-title"
                aria-describedby={view.message ? 'map-block-message' : undefined}
            >
                <div id="map-block-title" className="map-block-title">
                    {issue.kind === 'tracts-failed' ? <WarningMark /> : issue.kind !== 'not-configured' && <ErrorMark />}
                    <span>{view.title}</span>
                </div>
                {view.message && (
                    <div id="map-block-message" className="map-block-message">
                        {view.message}
                    </div>
                )}
                {view.canRetry && onRetry && (
                    <button type="button" className="map-block-retry" onClick={onRetry}>
                        Try again
                    </button>
                )}
                <ErrorDetails lines={details} />
            </div>
        </div>
    );
};

/* A map that works with something missing: a slim notice over its top edge, dismissible. */
export const MapNotice: React.FC<{ issue: IMapIssue; onDismiss: () => void }> = ({ issue, onDismiss }) => {
    const view = mapIssueView(issue);
    return (
        <div className="map-notice" role="status">
            <span className="map-notice-text">
                <strong>{view.title}.</strong> {view.message}
            </span>
            <button type="button" className="map-notice-close" onClick={onDismiss} aria-label="Dismiss">
                &times;
            </button>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/MapStatus.tsx